import Octicon, { Check } from '@githubprimer/octicons-react';
import moment from 'moment';
import * as React from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import {
	Button,
	ButtonGroup,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
} from 'reactstrap';
import { EntityVersionType, SubmissionState, EntityVersion, AppSiteDto, RedirectData } from 'types';
import { DateTime } from './fields';
import withEditContext, { HasEditContext } from './withEditContext';
import { RemovalException, getHandleableException } from 'types/Exceptions';
import EditErrorAlertPopup from './EditErrorAlertPopup';
import { Direction } from 'reactstrap/lib/Dropdown';
import { AxiosError } from 'axios';
import AppState from 'AppState';
import RecordScope from 'types/__models__/RecordScope';
import Redirect301OnTheFlyForm from 'features/redirects-301/Redirect301OnTheFlyForm';
import Confirm from '../Confirm';
import { ValidationErrorSource } from 'util/Misc';
import { areRedirectsEqual } from '../Utils';
import { ModelStatus } from '../../../../../Roc.SharedClientApp/types/model-status';
import { GetScopedSites } from 'util/AppStateUtils';
import ValidationErrorAlert, { AlertType } from '~/components/shared/edit/ValidationErrorAlert';

interface EditSaveButtonProps extends Pick<FormRenderProps, 'handleSubmit'>, HasEditContext {
	/**
	 * Controls whether or not this component is disabled.
	 */
	disabled: boolean;
	/**
	 * The direction of the dropdown menu
	 */
	dropdownMenuDirection?: Direction;
	/**
	 * Did an error occur during form submission?
	 */
	submitError: string | string[] | null;
	/**
	 * If set to true, this entity is flagged as a permanent entity, and therefore it cannot be inactivated.
	 */
	isPermanent: boolean;
}

enum ModalState {
	/**
	 * No modals are open.
	 */
	Closed = 0,
	/**
	 * Deletion of an entity failed due to a delete guard. The user will need to force deletion to proceed.
	 */
	RemovalErrorOpen,
	/**
	 * A save error occurred.
	 */
	SaveErrorOpen,
	/**
	 * The redirects 301 modal is open.
	 */
	RedirectsOpen,
}

interface EditSaveButtonState {
	/**
	 * Is the force changes warning modal open? This can happen when publishing as inactive on an existing record with foreign keys
	 */
	modalState: ModalState;
	/**
	 * The exception that occurred (if any) when attempting to push the change to the server.
	 */
	axiosError: AxiosError | null;
	/**
	 * The submission parameters of the last failed (but handleable) request.
	 */
	failedSubmitParameters?: SubmissionState;
	/**
	 * Is the publish options dropdown open?
	 */
	dropdownOpen: boolean;
	/**
	 * Is the schedule save modal open?
	 */
	scheduleModalOpen: boolean;
	/**
	 * Is the confirm publish modal open?
	 */
	confirmPublishModalOpen: boolean;
	/**
	 * Will the confirmed publish continue editing after confirmation?
	 */
	confirmPublishAndContinue: boolean;
	/**
	 * When initializing the confirm publish modal, this status will specify what type of
	 * status is being submitted when the confirm publish modal is confirmed.
	 */
	confirmPublishStatus: ModelStatus | null;
	/**
	 * When initializing the schedule modal, this status will specify what type of status
	 * is being submitted when the schedule modal is confirmed.
	 */
	scheduleModalStatus: ModelStatus | null;
	/**
	 * When initializing the schedule modal, this value is used to pre-populate the datetime field within
	 * the modal. Used when modifying the scheduled time of a future version.
	 */
	scheduleModalTime: moment.Moment | null;

	/**
	 * The next submission state to use after the 301 redirects validation
	 */
	nextSubmissionState?: SubmissionState;

	/**
	 * A list of site names that should match the `__redirects` field array.
	 * This is used to display the label for each 301 a user might create when the 301
	 * modal is open.
	 */
	siteNamesFor301s: string[];

	/**
	 * The initial form values to populate the redirect creation modal with.
	 */
	initialRedirectValues: RedirectData[];
}

class EditSaveButton extends React.Component<EditSaveButtonProps, EditSaveButtonState> {
	constructor(props) {
		super(props);

		this.state = {
			modalState: ModalState.Closed,
			failedSubmitParameters: undefined,
			dropdownOpen: false,
			scheduleModalOpen: false,
			confirmPublishModalOpen: false,
			confirmPublishAndContinue: false,
			confirmPublishStatus: null,
			scheduleModalStatus: null,
			scheduleModalTime: null,
			axiosError: null,
			siteNamesFor301s: [],
			initialRedirectValues: [],
		};
	}

	public componentDidUpdate(prevProps: EditSaveButtonProps) {
		const {
			editContext: { validationErrorSource, isLoading },
		} = this.props;

		if (validationErrorSource !== prevProps.editContext.validationErrorSource) {
			// if the redirect validation state changed, we potentially need to toggle the 301 modal
			if (validationErrorSource & ValidationErrorSource.EditForm) {
				// if the source of validation errors includes the edit form, lets tend to those first and hide any modals

				// NOTE: knowingly disabling this warning as we're confident setting this state in response to the context
				// prop changing won't cause an update loop
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({ modalState: ModalState.Closed });
			}
		}

		if (!prevProps.editContext.isLoading && isLoading) {
			// if we just started loading the entity, ensure that our modals are closed.

			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({ modalState: ModalState.Closed });
		}
	}

	/**
	 * Primary submission function. Submits a CreateEdit request.
	 *
	 * @private
	 * @memberof EditSaveButton
	 */
	private submit = async (params: SubmissionState) => {
		await this.props.editContext.setNextSubmissionState(params);

		try {
			await this.props.handleSubmit();

			// Submission succeeded so the force modal needs to close if it was open
			this.setState({ modalState: ModalState.Closed });
		} catch (error) {
			const serverException = getHandleableException(error) as RemovalException;

			if (serverException && serverException.exceptionCode === 'illegal_removal_operation') {
				this.setState({
					modalState: ModalState.RemovalErrorOpen,
					axiosError: error,
					failedSubmitParameters: params,
				});
			} else {
				this.setState({
					modalState: ModalState.SaveErrorOpen,
					axiosError: error,
				});
			}
		}
	};

	private forceLastFailedSubmit = async () => {
		const params = this.state.failedSubmitParameters;

		if (params) {
			const newParams = { ...params };
			newParams.force = true;
			await this.submit(newParams);
		}
	};

	private onForceCancel = () => {
		this.setState({ modalState: ModalState.Closed });
	};

	/**
	 * Toggles the publish options dropdown.
	 */
	public toggleDropdown = () => {
		this.setState((prevState) => ({
			dropdownOpen: !prevState.dropdownOpen,
		}));
	};

	/**
	 * Toggles the schedule modal.
	 *
	 * @param status - If specified, controls what status the entity will be published at when the schedule
	 * modal is confirmed.
	 * @param scheduledTime - If specified, pre-populates the datetime field on the schedule modal with this value.
	 * This is used when modifying the scheduled time of a future version.
	 */
	public toggleScheduleModal = (status?: ModelStatus | null, scheduledTime?: moment.Moment | null) => {
		this.setState((prevState) => ({
			scheduleModalOpen: !prevState.scheduleModalOpen,
			dropdownOpen: false,
			scheduleModalStatus: status || null,
			scheduleModalTime: scheduledTime || null,
		}));
	};

	/**
	 * Toggles the confirm modal.
	 *
	 * @param status - If specified, controls what status the entity will be published at when the publish confirmation
	 * modal is submitted.
	 * @param doContinue - If specified, controls whether or not the submission of the publish confirmation modal will
	 * trigger the "publish & continue" logic for the edit form.
	 */
	public toggleConfirmPublishModal = (status?: ModelStatus | null, doContinue?: boolean) => {
		this.setState((prevState) => ({
			confirmPublishModalOpen: !prevState.confirmPublishModalOpen,
			dropdownOpen: false,
			confirmPublishAndContinue: doContinue || false,
			confirmPublishStatus: status || null,
		}));
	};

	/**
	 * Toggles the 301 creation modal that appears if the entity had its scope changed and it needs 301 redirects
	 * created.
	 */
	public toggle301CreationModal = (doContinue?: boolean) => {
		this.setState((prevState) => ({
			modalState:
				prevState.modalState === ModalState.RedirectsOpen
					? // if the redirects modal is open, close it
					  ModalState.Closed
					: // otherwise open the redirects modal
					  ModalState.RedirectsOpen,
			confirmPublishAndContinue: doContinue || false,
		}));
	};

	/**
	 * Called when the scheduled modal is submitted.
	 */
	private doScheduledSubmit = async (formValues: any): Promise<void | Record<string, unknown>> => {
		// extract the scheduled time from the schedule form
		const { scheduledTime } = formValues;

		this.setState({ scheduleModalOpen: false });

		// `scheduleModalStatus` is set when the schedule modal is opened via `toggleScheduleModal`
		await this.submit({
			submissionType: this.state.scheduleModalStatus,
			submissionTime: scheduledTime,
			doContinue: false,
		});
	};

	private doSubmitWith301Redirects = async () => {
		await this.props.editContext.setNextSubmissionState({
			submissionType: null,
			submissionTime: null,
			doContinue: this.state.confirmPublishAndContinue,
		});

		this.props.handleSubmit();
	};

	/**
	 * Decides if the 301 redirect form should be hidden.
	 */
	private hide301Form = (): boolean => {
		const {
			editContext,
			editContext: { values },
		} = this.props;

		if (editContext.entityDefinition.hideCreate301OnTheFly) {
			return true;
		}

		if (values?.url == null) {
			// Entities without a url do not need a 301 redirect
			return true;
		}

		if (values?.status !== ModelStatus.Published) {
			// Entities that are not published do not need a 301 redirect
			return true;
		}

		return false;
	};

	/**
	 * Calculate 301 sites based on the scope change
	 */
	private handleScopeChange = (
		originalScope: RecordScope | undefined,
		currentScope: RecordScope | undefined,
	): boolean => {
		let currentSites: AppSiteDto[] = [];

		if (!currentScope) {
			currentSites = AppState.sites;
		} else if (currentScope.type === 'Site') {
			const currentSite = AppState.sites.find((s) => s.id === currentScope.value);
			if (currentSite) {
				currentSites.push(currentSite);
			}
		} else if (currentScope.type === 'Site Collection') {
			const currentSiteCollection = AppState.siteCollections.find((sc) => sc.id === currentScope.value);
			if (currentSiteCollection) {
				currentSites = currentSiteCollection.sites
					.map((site) => AppState.sites.find((s) => s.id === site.siteId))
					.filter((s) => s !== undefined) as AppSiteDto[];
			}
		}

		let redirect301Sites = GetScopedSites(originalScope);
		redirect301Sites = redirect301Sites.filter((rs) => currentSites.find((cs) => cs.id === rs.id) === undefined);

		if (!redirect301Sites || redirect301Sites.length === 0 || this.hide301Form()) {
			return false;
		}

		const initialFormValues = redirect301Sites.map((site) => ({
			redirectUrl: '', // initialize to an empty field value
			siteId: site.id,
		}));

		this.setState({
			// set the site names for each of the fields in the 301 redirects field array
			siteNamesFor301s: redirect301Sites.map((site) => site.name),
		});

		// populate the redirect form modal with the initial text fields to let users enter in any automatic
		// 301 creation
		this.setState({
			initialRedirectValues: initialFormValues,
		});

		return true;
	};

	/**
	 * Called when the default publish button is clicked.
	 *
	 * @param activeVersionType - The current active version type when the default publish button was clicked. This
	 * is used to control how the publish is performed as different behaviors happen depending on whether you're
	 * publishing an active record, a future record, etc.
	 */
	private doSubmit = async (
		e: React.MouseEvent<any>,
		activeVersion: EntityVersion | null,
		activeVersionType: EntityVersionType,
		status: ModelStatus,
		isFutureUpdate?: boolean,
		doContinue?: boolean,
	) => {
		const {
			editContext: { setNextSubmissionState, values, scopeChanged, originalScope, currentScope, isCreateMode },
		} = this.props;

		const updateFuture = activeVersionType === EntityVersionType.Future && isFutureUpdate;
		const submissionTime =
			updateFuture && activeVersion && activeVersion.scheduledTime ? activeVersion.scheduledTime : null;
		if (
			activeVersionType === EntityVersionType.Archived ||
			updateFuture ||
			(((values && values.status === ModelStatus.Deleted) || status === ModelStatus.Archived) &&
				activeVersionType !== EntityVersionType.Future)
		) {
			await setNextSubmissionState({
				submissionType: status,
				submissionTime: submissionTime,
				doContinue: doContinue ? true : false,
			});
		} else if (activeVersionType === EntityVersionType.Future) {
			// immediately publishing a future version, for this we want to first confirm that the user really
			// intends to do this
			this.toggleConfirmPublishModal(ModelStatus.Published, /* doContinue */ false);

			// and prevent submission
			e.preventDefault();
		}

		if (scopeChanged && !isCreateMode) {
			// if the scope value changed, determine if the change will remove the record from sites

			if (this.handleScopeChange(originalScope, currentScope)) {
				// this scope change will affect the url of the entity on some sites, so we need to
				// display the 301 creation modal
				this.toggle301CreationModal();

				// prevent submission
				e.preventDefault();
			}
		}

		// in all other scenarios, the entity is being updated and the status isn't changing
		// nothing needs to be done here as the form submission will continue
	};

	/**
	 * Helper for submitting the edit form and saving the current entity with a certain status.
	 *
	 * @param submissionType - The type of model status that the entity should be saved as.
	 */
	private saveAs = async (submissionType: ModelStatus | null) => {
		await this.submit({
			submissionType,
			submissionTime: null,
			doContinue: false,
		});
	};

	/**
	 * Helper for submitting the edit form and saving the current entity with a certain status and then
	 * having the app stay on the edit page.
	 *
	 * @param submissionType - The type of model status that the entity should be saved as.
	 * @param activeVersion - The active edited version of the entity
	 * @param activeVersionType - The type of the active edited version of the entity
	 * @param isFutureUpdate - True when this is a future scheduled entry being updated
	 */
	private saveAsAndContinue = async (
		submissionType: ModelStatus | null,
		activeVersion: EntityVersion | null,
		activeVersionType: EntityVersionType,
		isFutureUpdate?: boolean,
	) => {
		const {
			editContext: { setContinueWithHistory, scopeChanged, originalScope, currentScope, isCreateMode },
		} = this.props;

		const updateFuture =
			activeVersionType === EntityVersionType.Future &&
			isFutureUpdate &&
			(submissionType === ModelStatus.Published || submissionType === null);
		const submissionTime =
			updateFuture && activeVersion && activeVersion.scheduledTime ? activeVersion.scheduledTime : null;
		const updateDraft = activeVersionType === EntityVersionType.Draft;

		if (scopeChanged && !isCreateMode) {
			// if the scope value changed, determine if the change will remove the record from sites

			if (this.handleScopeChange(originalScope, currentScope)) {
				// this scope change will affect the url of the entity on some sites, so we need to
				// display the 301 creation modal
				this.toggle301CreationModal(/* doContinue */ true);

				return;
			}
		}

		setContinueWithHistory(updateFuture || updateDraft ? true : false);
		await this.submit({
			submissionType,
			submissionTime: submissionTime,
			doContinue: true,
		});
	};

	/**
	 * Called after the confirm publish dialog has been submitted.
	 */
	private continueWithPublish = async () => {
		this.setState({
			confirmPublishModalOpen: false,
		});

		// `confirmPublishStatus` and `confirmPublishAndContinue` is populated when the confirmation modal is opened
		// via `toggleConfirmPublishModal`

		await this.submit({
			submissionType: this.state.confirmPublishStatus,
			submissionTime: null,
			doContinue: this.state.confirmPublishAndContinue,
		});
	};

	public render() {
		const {
			editContext,
			editContext: { values, entityDefinition, isCreateMode },
			dropdownMenuDirection = 'down',
		} = this.props;

		let activeVersionType = EntityVersionType.Current;

		const activeVersion = editContext.getActiveVersion();
		if (activeVersion) {
			// if a version is active, then pull the type from there
			activeVersionType = activeVersion.type;
		}

		let recordName: string | null = null;
		let status: ModelStatus = ModelStatus.Published;

		if (values) {
			recordName = ` (${values.name ? values.name : values[entityDefinition.userFriendlyNameField]})`;

			if (values.status) {
				status =
					(values.status === ModelStatus.Deleted && activeVersionType !== EntityVersionType.Future) ||
					activeVersionType === EntityVersionType.Archived
						? ModelStatus.Published
						: values.status;
			}
		}

		let buttonColor = 'primary';

		if (
			((activeVersionType === EntityVersionType.Current || activeVersionType === EntityVersionType.Draft) &&
				(status === ModelStatus.Inactive || status === ModelStatus.Draft)) ||
			activeVersionType === EntityVersionType.Future
		) {
			buttonColor = 'muted';
		}

		const isFutureUpdate = activeVersionType === EntityVersionType.Future;
		const canSaveAsInactive = entityDefinition.canSaveAsInactive && !this.props.isPermanent;

		return (
			<div className="edit-form__actions-bar-item">
				<ButtonGroup>
					<Button
						type="submit"
						color={`${buttonColor}`}
						className="btn-medium"
						disabled={this.props.disabled}
						onClick={(e: React.MouseEvent<any>) =>
							this.doSubmit(e, activeVersion, activeVersionType, status, isFutureUpdate)
						}
						data-testid="edit-publish"
					>
						<div className="btn-icon-text-container">
							<span className="btn-icon-bg">
								<Octicon icon={Check} ariaLabel="Checkmark" />
							</span>
							<span className="btn-icon-text">
								{activeVersionType === EntityVersionType.Archived ||
								(values &&
									values.status === ModelStatus.Deleted &&
									activeVersionType !== EntityVersionType.Future) ||
								status === ModelStatus.Archived
									? // when editing a deleted version, show restore
									  'Restore & Publish'
									: // in this case, the version type will only be published or draft
									status === ModelStatus.Inactive ||
									  status === ModelStatus.Draft ||
									  activeVersionType === EntityVersionType.Future
									? // inactive/draft/future versions are updated by default, which maintains their status
									  'Update'
									: entityDefinition.publishButtonLabel}
							</span>
						</div>
					</Button>

					<Dropdown
						isOpen={this.state.dropdownOpen}
						toggle={this.toggleDropdown}
						className="dropdown--right"
						direction={dropdownMenuDirection}
					>
						<DropdownToggle
							color={`${buttonColor}`}
							caret
							className="dropdown-toggle-no-text dropdown-toggle"
							disabled={this.props.disabled}
							data-testid="edit-dropdown-button"
						>
							<span className="visually-hidden">
								{this.state.dropdownOpen ? 'Close' : 'Open'} Publish Options
							</span>
						</DropdownToggle>
						<DropdownMenu>
							{isCreateMode ? (
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(
													null,
													activeVersion,
													activeVersionType,
													isFutureUpdate,
												)
											}
											data-testid="edit-dropdown-publish-and-continue-create"
										>
											{entityDefinition.publishButtonLabel} &amp; Continue
										</DropdownItem>
									) : null}
									{entityDefinition.canPublishBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Published,
													/* scheduledTime */ null,
												)
											}
											data-testid="edit-dropdown-schedule-publish-create"
										>
											Publish At
										</DropdownItem>
									) : null}
									{canSaveAsInactive ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Inactive)}
											data-testid="edit-dropdown-save-as-inactive-create"
										>
											Save as Inactive
										</DropdownItem>
									) : null}
								</>
							) : activeVersion && activeVersionType === EntityVersionType.Future ? (
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(
													null,
													activeVersion,
													activeVersionType,
													isFutureUpdate,
												)
											}
											data-testid="edit-dropdown-update-and-continue-future"
										>
											Update &amp; Continue
										</DropdownItem>
									) : null}
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() => this.toggleConfirmPublishModal(ModelStatus.Published, false)}
											data-testid="edit-dropdown-publish-future"
										>
											{values?.status !== ModelStatus.Deleted ? 'Publish' : 'Restore & Publish'}
										</DropdownItem>
									) : null}
									{entityDefinition.canPublishBeScheduled &&
									values?.status !== ModelStatus.Deleted ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Published,
													activeVersion.scheduledTime,
												)
											}
											data-testid="edit-dropdown-schedule-publish-future"
										>
											Publish At
										</DropdownItem>
									) : null}
									{canSaveAsInactive && entityDefinition.canInactivationBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Inactive,
													activeVersion.scheduledTime,
												)
											}
											data-testid="edit-dropdown-schedule-save-as-inactive-current"
										>
											Save as Inactive At
										</DropdownItem>
									) : null}
									<DropdownItem
										onClick={() =>
											this.toggleConfirmPublishModal(ModelStatus.Draft, /* doContinue */ false)
										}
										data-testid="edit-dropdown-save-as-draft-future"
									>
										Save as Draft
									</DropdownItem>
								</>
							) : activeVersionType === EntityVersionType.Archived ||
							  (values && values.status === ModelStatus.Deleted) ? (
								// old/deleted versions
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(
													ModelStatus.Published,
													activeVersion,
													activeVersionType,
												)
											}
											data-testid="edit-dropdown-publish-and-continue-archived"
										>
											Restore, Publish &amp; Continue
										</DropdownItem>
									) : null}
									{entityDefinition.canPublishBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Published,
													/* scheduledTime */ null,
												)
											}
											data-testid="edit-dropdown-schedule-publish-archived"
										>
											Restore &amp; Publish At
										</DropdownItem>
									) : null}
									{entityDefinition.versionable ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Draft)}
											data-testid="edit-dropdown-save-as-draft-archived"
										>
											Save as Draft
										</DropdownItem>
									) : null}
									<DropdownItem
										onClick={() => this.saveAs(ModelStatus.Inactive)}
										data-testid="edit-dropdown-save-as-inactive-archived"
									>
										Restore as Inactive
									</DropdownItem>
								</>
							) : // current or draft/inactive versions
							status === ModelStatus.Inactive ? (
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(null, activeVersion, activeVersionType)
											}
											data-testid="edit-dropdown-update-and-continue-inactive"
										>
											Update &amp; Continue
										</DropdownItem>
									) : null}
									<DropdownItem
										onClick={() => this.saveAs(ModelStatus.Published)}
										data-testid="edit-dropdown-publish-inactive"
									>
										Publish
									</DropdownItem>
									{entityDefinition.canPublishBeScheduled ? (
										<DropdownItem
											onClick={() => this.toggleScheduleModal(ModelStatus.Published)}
											data-testid="edit-dropdown-schedule-publish-inactive"
										>
											Publish At
										</DropdownItem>
									) : null}
									{canSaveAsInactive && entityDefinition.canInactivationBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(ModelStatus.Inactive, /* scheduledTime */ null)
											}
											data-testid="edit-dropdown-schedule-save-as-inactive-current"
										>
											Save as Inactive At
										</DropdownItem>
									) : null}
									{entityDefinition.versionable ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Draft)}
											data-testid="edit-dropdown-save-as-draft-inactive"
										>
											Save as Draft
										</DropdownItem>
									) : null}
								</>
							) : status === ModelStatus.Draft ? (
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(null, activeVersion, activeVersionType)
											}
											data-testid="edit-dropdown-update-and-continue-draft"
										>
											Update &amp; Continue
										</DropdownItem>
									) : null}
									<DropdownItem
										onClick={() => this.saveAs(ModelStatus.Published)}
										data-testid="edit-dropdown-publish-draft"
									>
										Publish
									</DropdownItem>
									{entityDefinition.canPublishBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Published,
													/* scheduledTime */ null,
												)
											}
											data-testid="edit-dropdown-schedule-publish-draft"
										>
											Publish At
										</DropdownItem>
									) : null}
									{canSaveAsInactive ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Inactive)}
											data-testid="edit-dropdown-save-as-inactive-draft"
										>
											Save as Inactive
										</DropdownItem>
									) : null}
									{canSaveAsInactive && entityDefinition.canInactivationBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(ModelStatus.Inactive, /* scheduledTime */ null)
											}
											data-testid="edit-dropdown-schedule-save-as-inactive-current"
										>
											Save as Inactive At
										</DropdownItem>
									) : null}
								</>
							) : (
								// editing a current and published record
								<>
									{entityDefinition.canSaveAndContinue ? (
										<DropdownItem
											onClick={() =>
												this.saveAsAndContinue(null, activeVersion, activeVersionType)
											}
											data-testid="edit-dropdown-publish-and-continue-current"
										>
											{entityDefinition.publishButtonLabel} &amp; Continue
										</DropdownItem>
									) : null}
									{entityDefinition.canPublishBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(
													ModelStatus.Published,
													/* scheduledTime */ null,
												)
											}
											data-testid="edit-dropdown-schedule-publish-current"
										>
											Publish At
										</DropdownItem>
									) : null}
									{canSaveAsInactive ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Inactive)}
											data-testid="edit-dropdown-save-as-inactive-current"
										>
											Save as Inactive
										</DropdownItem>
									) : null}
									{canSaveAsInactive && entityDefinition.canInactivationBeScheduled ? (
										<DropdownItem
											onClick={() =>
												this.toggleScheduleModal(ModelStatus.Inactive, /* scheduledTime */ null)
											}
											data-testid="edit-dropdown-schedule-save-as-inactive-current"
										>
											Save as Inactive At
										</DropdownItem>
									) : null}
									{entityDefinition.versionable ? (
										<DropdownItem
											onClick={() => this.saveAs(ModelStatus.Draft)}
											data-testid="edit-dropdown-save-as-draft-current"
										>
											Save as Draft
										</DropdownItem>
									) : null}
								</>
							)}
						</DropdownMenu>
					</Dropdown>
				</ButtonGroup>

				<Modal
					isOpen={this.state.scheduleModalOpen}
					toggle={() => this.toggleScheduleModal()}
					className="edit-save-delete-modal modal-lg"
				>
					<Form
						onSubmit={this.doScheduledSubmit}
						initialValues={{ scheduledTime: this.state.scheduleModalTime }}
						render={(props) => {
							const { handleSubmit, submitError, submitting } = props;

							return (
								<form onSubmit={handleSubmit}>
									<ModalHeader toggle={() => this.toggleScheduleModal()}>
										{this.state.scheduleModalTime === null ? (
											<>Select Scheduled Date</>
										) : (
											<>Change Scheduled Date</>
										)}
									</ModalHeader>
									<ModalBody>
										{this.state.scheduleModalTime === null ? (
											// if the initial time for the modal is null, then we're scheduling
											// a publish for a current record
											<span data-testid="modal-body">
												When would you like to schedule your changes to this{' '}
												{this.props.editContext.entityDefinition.singularName}
												{recordName}?
											</span>
										) : (
											// otherwise, we're modifying the scheduled time for an existing future
											// version
											<span data-testid="modal-body">
												When would you like to reschedule your changes to this{' '}
												{this.props.editContext.entityDefinition.singularName}
												{recordName}?
											</span>
										)}

										<ValidationErrorAlert submitError={submitError} alertType={AlertType.Edit} />
										<span className="visually-hidden">Scheduled submission time</span>
										<Field name="scheduledTime" component={DateTime} />
									</ModalBody>
									<ModalFooter>
										<Button
											className="btn-outline-primary-alt btn-width-large"
											outline
											onClick={() => this.toggleScheduleModal()}
										>
											Cancel
										</Button>{' '}
										<Button
											type="submit"
											color="primary"
											className="btn-width-large"
											disabled={submitting}
											data-testid="submit-scheduled-date"
										>
											OK
										</Button>
									</ModalFooter>
								</form>
							);
						}}
					/>
				</Modal>

				<Modal isOpen={this.state.confirmPublishModalOpen} toggle={() => this.toggleConfirmPublishModal()}>
					<ModalHeader toggle={() => this.toggleConfirmPublishModal()}>
						{this.state.confirmPublishStatus === ModelStatus.Draft ? (
							<>Confirm Immediate Draft</>
						) : (
							<>Confirm Immediate Publish</>
						)}
					</ModalHeader>
					<ModalBody>
						{this.state.confirmPublishStatus === ModelStatus.Draft ? (
							<>
								Are you sure you wish to save your schedule changes to this{' '}
								{this.props.editContext.entityDefinition.singularName}
								{recordName}?<br />
								<br />
								Note: these scheduled changes will be immediately applied and this{' '}
								{this.props.editContext.entityDefinition.singularName} will be saved as a draft.
							</>
						) : (
							<>
								Are you sure you wish to immediately publish your changes to this{' '}
								{this.props.editContext.entityDefinition.singularName}
								{recordName}?<br />
								<br />
								Note: these scheduled changes will be immediately applied and this{' '}
								{this.props.editContext.entityDefinition.singularName} will be published.
							</>
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							className="btn-outline-primary-alt btn-width-large"
							outline
							onClick={() => this.toggleConfirmPublishModal()}
						>
							Cancel
						</Button>{' '}
						<Button
							color="primary"
							className="btn-width-large"
							onClick={this.continueWithPublish}
							data-testid="confirm-imediate-publish"
						>
							OK
						</Button>
					</ModalFooter>
				</Modal>

				<EditErrorAlertPopup
					axiosError={this.state.axiosError}
					isOpen={this.state.modalState === ModalState.RemovalErrorOpen}
					onCancel={this.onForceCancel}
					onConfirm={this.forceLastFailedSubmit}
					inProgress={false}
				/>

				<Field name="__redirects" subscription={{ dirty: true }} isEqual={areRedirectsEqual}>
					{(props) => {
						const {
							meta: { dirty },
						} = props;

						return (
							<Confirm
								size="medium"
								color="primary"
								isOpen={this.state.modalState === ModalState.RedirectsOpen}
								onCancel={() => this.toggle301CreationModal()}
								onConfirm={() => this.doSubmitWith301Redirects()}
								renderTitle={() => 'Confirm Publish'}
								confirmButtonText={dirty ? 'Publish' : 'Skip & Publish'}
								renderContents={() => (
									<>
										<h4>Are you sure you want to change the scope of this item?</h4>
										<p>
											If you would like to create site specific 301s from the URL of the item, to
											a new URL, please enter them below.
										</p>

										<Redirect301OnTheFlyForm
											record={this.props.editContext.values}
											initialValue={this.state.initialRedirectValues}
											siteNames={this.state.siteNamesFor301s}
											submitError={this.props.submitError}
										/>
									</>
								)}
							/>
						);
					}}
				</Field>
			</div>
		);
	}
}

export { EditSaveButton as EditSaveButtonWrapped };
export default withEditContext(EditSaveButton);
